import api from './apiClient';

/**
 * Helper to build FormData from an object, handling File objects and nested values.
 * @param {Object} data - Data object with fields to include
 * @param {File|null} bannerImage - Optional banner image file
 * @returns {FormData}
 */
const buildFormData = (data, bannerImage = null) => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (value instanceof File) {
      formData.append(key, value);
    } else if (typeof value === 'boolean') {
      formData.append(key, value.toString());
    } else if (typeof value === 'number') {
      formData.append(key, value.toString());
    } else if (value instanceof Date) {
      formData.append(key, value.toISOString());
    } else {
      formData.append(key, value);
    }
  });

  if (bannerImage instanceof File) {
    formData.append('bannerImage', bannerImage);
  }

  return formData;
};

// ===================
// Event CRUD (Admin/ContentCreator/MasterTrainer)
// ===================

/**
 * Get paginated list of events for management
 * @param {Object} params - Query params (page, pageSize, search, isPublished, isFeatured, fromDate, toDate)
 * @param {Object} options - Request options
 */
export const getEvents = async (params = {}, options = {}) => {
  return api.get('/events', params, options);
};

/**
 * Get event details by ID
 * @param {number} id - Event ID
 * @param {Object} options - Request options
 */
export const getEvent = async (id, options = {}) => {
  return api.get(`/events/${id}`, {}, options);
};

/**
 * Create a new event with optional banner image
 * @param {Object} data - Event data
 * @param {File|null} bannerImage - Optional banner image
 * @param {Object} options - Request options
 */
export const createEvent = async (data, bannerImage = null, options = {}) => {
  const formData = buildFormData(data, bannerImage);
  return api.upload('/events', formData, options);
};

/**
 * Update an event with optional banner image
 * @param {number} id - Event ID
 * @param {Object} data - Event data (all fields optional for partial update)
 * @param {File|null} bannerImage - Optional new banner image
 * @param {Object} options - Request options
 */
export const updateEvent = async (id, data, bannerImage = null, options = {}) => {
  const formData = buildFormData(data, bannerImage);
  return api.uploadPut(`/events/${id}`, formData, options);
};

/**
 * Delete an event
 * @param {number} id - Event ID
 * @param {Object} options - Request options
 */
export const deleteEvent = async (id, options = {}) => {
  return api.delete(`/events/${id}`, options);
};

// ===================
// Event Registration Management (Admin/Owner)
// ===================

/**
 * Get paginated list of registrations for an event
 * @param {number} eventId - Event ID
 * @param {Object} params - Query params (page, pageSize, search)
 * @param {Object} options - Request options
 */
export const getEventRegistrations = async (eventId, params = {}, options = {}) => {
  return api.get(`/events/${eventId}/registrations`, params, options);
};

/**
 * Delete a registration by admin/owner
 * @param {number} eventId - Event ID
 * @param {number} registrationId - Registration ID
 * @param {Object} options - Request options
 */
export const deleteEventRegistration = async (eventId, registrationId, options = {}) => {
  return api.delete(`/events/${eventId}/registrations/${registrationId}`, options);
};

// ===================
// Public Event Endpoints
// ===================

/**
 * Get paginated list of published events for public viewing
 * @param {Object} params - Query params (page, pageSize, search, fromDate, toDate)
 * @param {Object} options - Request options
 */
export const getPublicEvents = async (params = {}, options = {}) => {
  return api.get('/events/public', params, options);
};

/**
 * Get trending/featured events for public display
 * @param {number} limit - Maximum number of events to return
 * @param {Object} options - Request options
 */
export const getTrendingEvents = async (limit = 5, options = {}) => {
  return api.get('/events/public/trending', { limit }, options);
};

/**
 * Get upcoming events for public display
 * @param {number} limit - Maximum number of events to return
 * @param {Object} options - Request options
 */
export const getUpcomingEvents = async (limit = 10, options = {}) => {
  return api.get('/events/public/upcoming', { limit }, options);
};

/**
 * Get public event detail by ID
 * @param {number} id - Event ID
 * @param {Object} options - Request options
 */
export const getPublicEvent = async (id, options = {}) => {
  return api.get(`/events/public/${id}`, {}, options);
};

// ===================
// Public Registration Endpoints
// ===================

/**
 * Register for an event (public)
 * Returns registration with EditCode for future modifications
 * @param {number} eventId - Event ID
 * @param {Object} data - Registration data (email, firstName, lastName, phone?, remarks?)
 * @param {Object} options - Request options
 */
export const registerForEvent = async (eventId, data, options = {}) => {
  return api.post(`/events/public/${eventId}/register`, data, options);
};

/**
 * Check if an email is already registered for an event
 * @param {number} eventId - Event ID
 * @param {string} email - Email to check
 * @param {Object} options - Request options
 */
export const checkRegistration = async (eventId, email, options = {}) => {
  return api.get(`/events/public/${eventId}/registration/check`, { email }, options);
};

/**
 * Get own registration by email and edit code
 * @param {number} eventId - Event ID
 * @param {string} email - Registration email
 * @param {string} editCode - Edit code received upon registration
 * @param {Object} options - Request options
 */
export const getMyRegistration = async (eventId, email, editCode, options = {}) => {
  return api.get(`/events/public/${eventId}/registration`, { email, editCode }, options);
};

/**
 * Update own registration (requires edit code)
 * @param {number} eventId - Event ID
 * @param {Object} data - Update data (email, editCode required; firstName, lastName, phone, remarks optional)
 * @param {Object} options - Request options
 */
export const updateMyRegistration = async (eventId, data, options = {}) => {
  return api.put(`/events/public/${eventId}/registration`, data, options);
};

/**
 * Cancel own registration
 * @param {number} eventId - Event ID
 * @param {string} email - Registration email
 * @param {string} editCode - Edit code received upon registration
 * @param {Object} options - Request options
 */
export const cancelMyRegistration = async (eventId, email, editCode, options = {}) => {
  return api.delete(`/events/public/${eventId}/registration?email=${encodeURIComponent(email)}&editCode=${encodeURIComponent(editCode)}`, options);
};

/**
 * Request edit code recovery email (limited to once per 24 hours)
 * @param {number} eventId - Event ID
 * @param {string} email - Registration email
 * @param {Object} options - Request options
 */
export const requestEditCodeRecovery = async (eventId, email, options = {}) => {
  return api.post(`/events/public/${eventId}/registration/recover`, { email }, options);
};
